
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
} from '@tanstack/react-table';
import { useState, useRef, useEffect } from 'react';
import { ArrowUpDown, Search, Pencil, Clock, AlertTriangle, CheckCircle2, XCircle, CircleDot, Loader2, DollarSign, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ========================================
// Helper: valor mensal esperado de uma empresa
// Espelha apps/api/services/billing/billingMath.js (ciclo + descontos).
// - Plano anual cobrado mensalmente: base = yearlyPrice / 12
// - Desconto comercial incide só sobre o plano; retenção sobre o subtotal
// ========================================
function isCommercialDiscountActive(company) {
    return (
        company.commercialDiscountType &&
        company.commercialDiscountValue != null &&
        (!company.commercialDiscountExpiresAt ||
            new Date(company.commercialDiscountExpiresAt) > new Date())
    );
}

function computeExpectedMonthly(company) {
    const monthlyRate = Number(company.plan?.monthlyPrice || 0);
    const yearlyRate = company.plan?.yearlyPrice
        ? Number(company.plan.yearlyPrice) / 12
        : null;
    const basePlanPrice =
        company.billingCycle === 'anual' && yearlyRate != null
            ? yearlyRate
            : monthlyRate;

    // Desconto comercial — só sobre o plano
    let discountedPlan = basePlanPrice;
    if (isCommercialDiscountActive(company)) {
        const savings =
            company.commercialDiscountType === 'percent'
                ? basePlanPrice * (company.commercialDiscountValue / 100)
                : Math.min(company.commercialDiscountValue, basePlanPrice);
        discountedPlan = Math.max(0, basePlanPrice - savings);
    }

    const addonsTotal = (company.addons || []).reduce(
        (sum, a) => sum + Number(a.price || 0),
        0,
    );
    let total = discountedPlan + addonsTotal;

    // Desconto de retenção — sobre o subtotal
    if (company.retentionDiscountPercent && company.retentionDiscountExpiresAt) {
        if (new Date(company.retentionDiscountExpiresAt) > new Date()) {
            total = total * (1 - company.retentionDiscountPercent / 100);
        }
    }

    return {
        basePlanPrice,
        addonsTotal,
        total: Math.round(total * 100) / 100,
    };
}

// ========================================
// Helper: Trial badge
// ========================================
function TrialBadge({ trialInfo, status }) {
    if (!trialInfo && status !== 'trial') return null;

    // No trial info but status is trial (legacy)
    if (!trialInfo) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/15 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border border-yellow-500/30 dark:border-yellow-500/20">
                <Clock className="w-3 h-3" />
                Trial
            </span>
        );
    }

    if (trialInfo.isExpired) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/30 dark:border-red-500/20">
                <XCircle className="w-3 h-3" />
                Expirado
            </span>
        );
    }

    const daysText = trialInfo.daysRemaining === 1 ? '1 dia' : `${trialInfo.daysRemaining} dias`;
    const isUrgent = trialInfo.daysRemaining <= 3;

    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isUrgent
            ? 'bg-orange-500/15 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-500/30 dark:border-orange-500/20'
            : 'bg-emerald-500/15 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 dark:border-emerald-500/20'
            }`}>
            <Clock className="w-3 h-3" />
            {daysText}
        </span>
    );
}

// ========================================
// Helper: Financial status badge (com popover para vencidas)
// ========================================
function FinancialBadge({ billing }) {
    const [showPopover, setShowPopover] = useState(false);
    const popoverRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setShowPopover(false);
            }
        }
        if (showPopover) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showPopover]);

    if (!billing) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-foreground-muted/10 text-foreground-muted border border-border-strong/20">
                <Loader2 className="w-3 h-3 animate-spin" />
                ...
            </span>
        );
    }

    if (billing.error || !billing.found) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-foreground-muted/10 text-foreground-muted border border-border-strong/20">
                <CircleDot className="w-3 h-3" />
                Sem dados
            </span>
        );
    }

    const { financial } = billing;
    if (!financial) return null;

    const statusMap = {
        ok: {
            icon: <CheckCircle2 className="w-3 h-3" />,
            label: 'Normal',
            classes: 'bg-emerald-500/15 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 dark:border-emerald-500/20',
        },
        overdue: {
            icon: <AlertTriangle className="w-3 h-3" />,
            label: `${financial.overdueCount} vencida${financial.overdueCount > 1 ? 's' : ''}`,
            classes: 'bg-red-500/15 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/30 dark:border-red-500/20',
        },
        no_charges: {
            icon: <CircleDot className="w-3 h-3" />,
            label: 'S/ cobranças',
            classes: 'bg-foreground-muted/10 text-foreground-muted border border-border-strong/20',
        },
        error: {
            icon: <XCircle className="w-3 h-3" />,
            label: 'Erro',
            classes: 'bg-red-500/15 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/30 dark:border-red-500/20',
        },
    };

    const info = statusMap[financial.status] || statusMap.error;
    const hasOverdue = financial.status === 'overdue' && financial.overduePayments?.length > 0;

    return (
        <div className="relative" ref={popoverRef}>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    if (hasOverdue) setShowPopover(!showPopover);
                }}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${info.classes} ${hasOverdue ? 'cursor-pointer hover:opacity-80' : ''}`}
            >
                {info.icon}
                {info.label}
            </button>

            {/* Popover com detalhes das cobranças vencidas */}
            {showPopover && hasOverdue && (
                <div className="absolute z-50 top-full mt-1 left-0 w-72 bg-surface border border-border-strong rounded-lg shadow-xl p-3"
                    onClick={(e) => e.stopPropagation()}>
                    <div className="text-xs font-semibold text-foreground-secondary mb-2">
                        Cobranças Vencidas ({financial.overduePayments.length})
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                        {financial.overduePayments.map((p) => (
                            <div key={p.id} className="flex items-center justify-between bg-surface-active/50 rounded-md p-2 text-xs">
                                <div>
                                    <div className="text-foreground-secondary font-medium">
                                        R$ {Number(p.value || 0).toFixed(2)}
                                    </div>
                                    <div className="text-foreground-muted">
                                        Venc: {p.dueDate ? new Date(p.dueDate + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                                    </div>
                                </div>
                                {(p.invoiceUrl || p.bankSlipUrl) && (
                                    <a
                                        href={p.invoiceUrl || p.bankSlipUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-cyan-700 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 text-[11px] font-medium"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <ExternalLink className="w-3 h-3" />
                                        Pagar
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-border-strong text-xs text-foreground-muted">
                        Total: <span className="text-red-700 dark:text-red-400 font-semibold">R$ {Number(financial.totalOverdue || 0).toFixed(2)}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function CompanyTable({ data, isLoading, onEdit, billingData }) {
    const navigate = useNavigate();
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    const columns = [
        {
            accessorKey: 'companyName',
            header: ({ column }) => {
                return (
                    <button
                        className="flex items-center hover:text-cyan-700 dark:hover:text-cyan-400 transition-colors"
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                    >
                        Empresa
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </button>
                )
            },
            cell: ({ row }) => (
                <div>
                    <div className="font-medium text-foreground-strong">{row.getValue('companyName')}</div>
                </div>
            ),
        },
        {
            accessorKey: 'cnpj',
            header: 'CNPJ',
            cell: ({ row }) => {
                const cnpj = row.getValue('cnpj');
                const formatted = cnpj?.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') || cnpj;
                return <div className="text-foreground-muted font-mono text-xs">{formatted}</div>;
            },
        },
        {
            id: 'plan',
            header: 'Plano',
            accessorFn: (row) => row.plan?.name || row.planType || '—',
            cell: ({ row }) => {
                const planName = row.getValue('plan');
                const company = row.original;
                const isAnnual = company.billingCycle === 'anual';
                const hasCommercialDiscount = isCommercialDiscountActive(company);
                return (
                    <div className="flex flex-col gap-0.5">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/15 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border border-cyan-500/30 dark:border-cyan-500/20">
                            {planName}
                        </span>
                        <div className="flex items-center gap-1">
                            {isAnnual && (
                                <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">Anual</span>
                            )}
                            {hasCommercialDiscount && (
                                <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                                    -{company.commercialDiscountType === 'percent'
                                        ? `${company.commercialDiscountValue}%`
                                        : `R$${Number(company.commercialDiscountValue).toFixed(0)}`}
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            id: 'mrr',
            header: ({ column }) => (
                <button
                    className="flex items-center hover:text-cyan-700 dark:hover:text-cyan-400 transition-colors"
                    onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                >
                    MRR
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </button>
            ),
            accessorFn: (row) => {
                const billing = billingData?.[row.cnpj];
                const subValue = billing?.subscriptions?.[0]?.value;
                // Sem assinatura Asaas: usar o valor esperado por ciclo (não monthlyPrice cru)
                if (subValue != null) return subValue;
                return computeExpectedMonthly(row).total;
            },
            cell: ({ row }) => {
                const asaasValue = Number(row.getValue('mrr'));
                const company = row.original;

                // Expected MRR = base por ciclo + addons − descontos (helper único)
                const {
                    basePlanPrice: planPrice,
                    addonsTotal,
                    total: expectedMrr,
                } = computeExpectedMonthly(company);

                // Deviation: Asaas - expected (negative = underbilling)
                const billing = billingData?.[company.cnpj];
                const hasAsaas = billing?.found && billing?.subscriptions?.[0]?.value != null;
                const deviation = hasAsaas ? Math.round((asaasValue - expectedMrr) * 100) / 100 : null;

                return (
                    <div className="flex items-center gap-1.5">
                        <span className="text-emerald-700 dark:text-emerald-400 font-medium font-mono text-sm">
                            R$ {asaasValue.toFixed(2)}
                        </span>
                        {deviation !== null && deviation !== 0 && (() => {
                            const addonsInfo = addonsTotal > 0 ? ` + Addons: R$ ${addonsTotal.toFixed(2)}` : '';
                            const tooltip = `Esperado: R$ ${expectedMrr.toFixed(2)} (Plano: R$ ${planPrice.toFixed(2)}${addonsInfo})\nAsaas: R$ ${asaasValue.toFixed(2)}\nDesvio: ${deviation > 0 ? '+' : ''}R$ ${deviation.toFixed(2)}`;
                            return (
                                <span
                                    className={`inline-flex items-center px-1 py-0 rounded text-[10px] font-semibold leading-tight ${
                                        deviation < 0
                                            ? 'bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30'
                                            : 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30'
                                    }`}
                                    title={tooltip}
                                >
                                    {deviation > 0 ? '↑' : '↓'}{Math.abs(deviation).toFixed(0)}
                                </span>
                            );
                        })()}
                    </div>
                );
            },
        },
        {
            id: 'customerStatus',
            header: 'Tipo',
            cell: ({ row }) => {
                const cnpj = row.original.cnpj;
                const billing = billingData?.[cnpj];
                const confirmedCount = billing?.financial?.confirmedCount || 0;
                const isClient = confirmedCount > 0;

                if (row.original.status === 'churned' || row.original.status === 'suspended') {
                    return (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/30 dark:border-red-500/20">
                            <XCircle className="w-3 h-3" />
                            Churned
                        </span>
                    );
                }

                if (isClient) {
                    return (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 dark:border-emerald-500/20">
                            <CheckCircle2 className="w-3 h-3" />
                            Cliente
                        </span>
                    );
                }

                // Trial — show remaining days if available
                return <TrialBadge trialInfo={row.original.trialInfo} status={row.original.status} />;
            },
        },
        {
            id: 'financial',
            header: () => (
                <div className="flex items-center gap-1">
                    <DollarSign className="w-3.5 h-3.5" />
                    Status
                </div>
            ),
            cell: ({ row }) => {
                const cnpj = row.original.cnpj;
                const billing = billingData?.[cnpj];
                return <FinancialBadge billing={billing} />;
            },
        },
        {
            accessorKey: 'createdAt',
            header: 'Cliente desde',
            cell: ({ row }) => {
                const date = new Date(row.getValue('createdAt'));
                return <div className="text-foreground-muted text-sm">{date.toLocaleDateString('pt-BR')}</div>
            },
        },
        {
            id: 'whatsappActivation',
            header: 'Ativação WhatsApp',
            accessorFn: (row) => row.integrations?.[0]?.createdAt || null,
            cell: ({ row }) => {
                const raw = row.original.integrations?.[0]?.createdAt;
                if (!raw) {
                    return <span className="text-foreground-muted text-xs">—</span>;
                }
                const date = new Date(raw);
                const now = new Date();
                const isCurrentMonth =
                    date.getFullYear() === now.getFullYear() &&
                    date.getMonth() === now.getMonth();
                return (
                    <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            isCurrentMonth
                                ? 'bg-emerald-500/15 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 dark:border-emerald-500/20'
                                : 'bg-surface-active text-foreground-muted border-border'
                        }`}
                        title={isCurrentMonth ? 'Ativado neste mês' : undefined}
                    >
                        {date.toLocaleDateString('pt-BR')}
                    </span>
                );
            },
        },
        {
            id: 'actions',
            header: '',
            cell: ({ row }) => {
                const isPro = row.original.planType === 'Professional';
                return (
                    <div className="flex items-center justify-end gap-1">
                        {isPro && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/internal/implementation/${row.original.id}`);
                                }}
                                className="p-2 mr-2 hover:bg-indigo-900/50 rounded-lg transition-colors text-indigo-700 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium text-xs flex items-center gap-1 border border-indigo-900/50"
                                title="Gerenciar Implantação Pro"
                            >
                                Implantação
                            </button>
                        )}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(row.original);
                            }}
                            className="p-2 hover:bg-surface-hover rounded-full transition-colors text-foreground-muted hover:text-yellow-700 dark:hover:text-yellow-400"
                            title="Editar empresa"
                        >
                            <Pencil className="w-4 h-4" />
                        </button>
                    </div>
                )
            },
        },
    ];

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        state: {
            sorting,
            globalFilter,
        },
    });

    if (isLoading) {
        return <div className="p-8 text-center text-foreground-muted">Carregando dados...</div>;
    }

    return (
        <div className="w-full">
            {/* Search Input */}
            <div className="flex items-center py-4">
                <div className="relative max-w-sm w-full">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-foreground-muted" />
                    <input
                        placeholder="Filtrar empresas..."
                        value={globalFilter ?? ''}
                        onChange={(event) => setGlobalFilter(event.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-surface border border-border rounded-lg text-sm text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 transition-colors"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-foreground-muted uppercase bg-surface/50 border-b border-border">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} className="px-6 py-4 font-semibold tracking-wider">
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="divide-y divide-border bg-surface/20">
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                    className="hover:bg-surface-hover/50 transition-colors cursor-pointer group"
                                    onClick={() => navigate(`/internal/implementation/${row.original.id}`)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="px-6 py-4">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="h-24 text-center text-foreground-muted">
                                    Nenhuma empresa encontrada.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex-1 text-sm text-foreground-muted">
                    {table.getFilteredRowModel().rows.length} registros
                </div>
                <div className="space-x-2">
                    <button
                        className="px-3 py-1 text-sm border border-border rounded-md text-foreground-muted hover:text-foreground-strong hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        Anterior
                    </button>
                    <button
                        className="px-3 py-1 text-sm border border-border rounded-md text-foreground-muted hover:text-foreground-strong hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Próxima
                    </button>
                </div>
            </div>
        </div>
    );
}
